import { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../ui/use-toast';

interface CreateModuleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (moduleData: { moduleName: string; moduleDescription: string }) => void;
  subjectName: string;
}

const CreateModuleDialog = ({ open, onClose, onSave, subjectName }: CreateModuleDialogProps) => {
  const { toast } = useToast();
  const [moduleData, setModuleData] = useState({
    moduleName: '',
    moduleDescription: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!moduleData?.moduleName?.trim()) {
      toast({
        title: "Module name required",
        description: "Please enter a name for the module",
        variant: "destructive"
      });
      return;
    }

    onSave(moduleData);
    setModuleData({ moduleName: '', moduleDescription: '' });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add New Module</DialogTitle>
            <DialogDescription>
              Create a new module for {subjectName}. Fill in the module details below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Module Name</Label>
              <Input
                id="name"
                value={moduleData.moduleName}
                onChange={(e) => setModuleData(prev => ({ ...prev, moduleName: e.target.value }))}
                placeholder="e.g., Introduction to HTML"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="moduleDescription">Description</Label>
              <Textarea
                id="moduleDescription"
                value={moduleData.moduleDescription}
                onChange={(e) => setModuleData(prev => ({ ...prev, moduleDescription: e.target.value }))}
                placeholder="Describe what students will learn in this module"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" >Save Module</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateModuleDialog;